import { Controller } from "@hotwired/stimulus"

// The item form keeps its supplier list client-side: rows go in through a modal
// and come out as nested attributes, so a rejected save redisplays exactly what
// was entered. Rows are built as DOM nodes rather than markup strings —
// supplier names and item codes are user data.
export default class extends Controller {
  static targets = [
    "table", "rows", "empty", "inputs", "modal",
    "modalTitle", "supplier", "cost", "code", "error"
  ]
  static values = { entries: Array, currency: String }

  connect() {
    // Keys survive removals, which indexes do not — the modal edits a row by
    // key while the submitted attribute indexes come from array position.
    this.nextKey = 0
    this.entries = this.entriesValue.map((entry) => ({ ...entry, key: this.nextKey++ }))
    this.editingKey = null

    this.modal = window.bootstrap.Modal.getOrCreateInstance(this.modalTarget)
    this.onShown = () => this.supplierTarget.focus()
    this.modalTarget.addEventListener("shown.bs.modal", this.onShown)

    this.render()
  }

  disconnect() {
    this.modalTarget.removeEventListener("shown.bs.modal", this.onShown)
    this.modal?.dispose()
    this.modal = null
  }

  // --- modal -------------------------------------------------------------

  open() {
    this.editingKey = null
    this.modalTitleTarget.textContent = "Add Supplier"
    this.supplierTarget.value = ""
    this.costTarget.value = ""
    this.codeTarget.value = ""
    this.showModal()
  }

  edit(event) {
    const entry = this.entryFor(event)
    if (!entry) return

    this.editingKey = entry.key
    this.modalTitleTarget.textContent = "Edit Supplier"
    this.supplierTarget.value = entry.supplierId
    this.costTarget.value = entry.cost ?? ""
    this.codeTarget.value = entry.code ?? ""
    this.showModal()
  }

  showModal() {
    this.clearError()
    this.lockUsedSuppliers()
    this.modal.show()
  }

  close() {
    this.modal.hide()
  }

  // A supplier can hold only one set of terms per item, so keep the ones
  // already listed out of reach instead of failing on save.
  lockUsedSuppliers() {
    const taken = this.liveEntries()
      .filter((entry) => entry.key !== this.editingKey)
      .map((entry) => String(entry.supplierId))

    Array.from(this.supplierTarget.options).forEach((option) => {
      option.disabled = option.value !== "" && taken.includes(option.value)
    })
  }

  // Enter inside the modal would otherwise submit the item form, since the
  // modal sits inside it.
  submitOnEnter(event) {
    event.preventDefault()
    this.save()
  }

  save() {
    const supplierId = this.supplierTarget.value
    const option = this.supplierTarget.selectedOptions[0]
    const cost = this.costTarget.value.trim()

    if (!supplierId) return this.showError("Choose a supplier.")
    if (cost === "" || Number(cost) < 0 || Number.isNaN(Number(cost))) {
      return this.showError("Enter a cost price of zero or more.")
    }

    const values = {
      supplierId: supplierId,
      name: option.textContent.trim(),
      cost: cost,
      code: this.codeTarget.value.trim()
    }

    if (this.editingKey === null) {
      this.entries.push({ id: null, destroy: false, key: this.nextKey++, ...values })
    } else {
      const entry = this.entries.find((e) => e.key === this.editingKey)
      Object.assign(entry, values)
    }

    this.modal.hide()
    this.render()
  }

  // --- list --------------------------------------------------------------

  remove(event) {
    const entry = this.entryFor(event)
    if (!entry) return

    // A saved row has to be told to go; an unsaved one can just be dropped.
    if (entry.id) {
      entry.destroy = true
    } else {
      this.entries = this.entries.filter((e) => e.key !== entry.key)
    }

    this.render()
  }

  entryFor(event) {
    const key = Number(event.currentTarget.dataset.key)
    return this.entries.find((entry) => entry.key === key)
  }

  liveEntries() {
    return this.entries.filter((entry) => !entry.destroy)
  }

  render() {
    const live = this.liveEntries()

    this.tableTarget.hidden = live.length === 0
    this.emptyTarget.hidden = live.length > 0

    this.rowsTarget.replaceChildren(
      ...live.map((entry, index) => this.buildRow(entry, index === 0))
    )
    this.inputsTarget.replaceChildren(...this.buildInputs())
  }

  buildRow(entry, isPrimary) {
    const row = document.createElement("tr")

    const name = document.createElement("td")
    name.textContent = entry.name || ""
    if (isPrimary) {
      const badge = document.createElement("span")
      badge.className = "badge bg-light text-dark border fw-normal ms-2"
      badge.textContent = "Primary"
      name.appendChild(badge)
    }

    const code = document.createElement("td")
    code.className = "text-muted"
    code.textContent = entry.code || "—"

    const cost = document.createElement("td")
    cost.className = "text-end"
    cost.textContent = `${this.currencyValue}${Number(entry.cost).toFixed(2)}`

    const actions = document.createElement("td")
    actions.className = "text-end text-nowrap"
    actions.append(
      this.iconButton("bi-pencil", "btn-outline-secondary", "Edit supplier", "edit", entry.key),
      this.iconButton("bi-x-lg", "btn-outline-danger", "Remove supplier", "remove", entry.key)
    )

    row.append(name, code, cost, actions)
    return row
  }

  iconButton(icon, style, label, action, key) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = `btn btn-sm ${style} ms-1`
    button.title = label
    button.setAttribute("aria-label", label)
    button.dataset.action = `product-suppliers#${action}`
    button.dataset.key = key

    const glyph = document.createElement("i")
    glyph.className = `bi ${icon}`
    button.appendChild(glyph)

    return button
  }

  // Indexes come from position in the full list, removals included, so every
  // nested-attributes key stays distinct.
  buildInputs() {
    const inputs = []

    this.entries.forEach((entry, index) => {
      if (entry.destroy && !entry.id) return

      const base = `product[product_suppliers_attributes][${index}]`
      if (entry.id) inputs.push(this.hidden(`${base}[id]`, entry.id))

      if (entry.destroy) {
        inputs.push(this.hidden(`${base}[_destroy]`, "1"))
      } else {
        inputs.push(this.hidden(`${base}[supplier_id]`, entry.supplierId))
        inputs.push(this.hidden(`${base}[cost_price]`, entry.cost))
        inputs.push(this.hidden(`${base}[supplier_item_code]`, entry.code || ""))
      }
    })

    return inputs
  }

  hidden(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    return input
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }
}
